import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { 
  MessageSquare, 
  Send, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Info,
  Save,
  TestTube
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

const AVAILABLE_VARIABLES = [
  { name: "{customer_name}", description: "Customer's name" },
  { name: "{plan_name}", description: "Plan type name" },
  { name: "{phone_number}", description: "Customer's mobile number" },
  { name: "{plan_price}", description: "Plan price (e.g., 15.00)" },
  { name: "{renewal_date}", description: "Renewal date (YYYY-MM-DD)" },
];

export default function SMSSettings() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  
  const [formData, setFormData] = useState({
    enabled: true,
    alpha_tag: "",
    message_template: "",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const [settingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/sms-settings`, { headers }),
        axios.get(`${API}/sms-logs?limit=20`, { headers }),
      ]);
      setSettings(settingsRes.data);
      setFormData({
        enabled: settingsRes.data.enabled,
        alpha_tag: settingsRes.data.alpha_tag,
        message_template: settingsRes.data.message_template,
      });
      setLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching SMS settings:", error);
      toast.error("Failed to load SMS settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = getAuthHeader();
      await axios.put(`${API}/sms-settings`, formData, { headers });
      toast.success("SMS settings saved successfully");
      fetchData();
    } catch (error) {
      console.error("Error saving SMS settings:", error);
      toast.error("Failed to save SMS settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error("Please enter a phone number");
      return;
    }
    
    try {
      setTestSending(true);
      const headers = getAuthHeader();
      await axios.post(`${API}/sms-settings/test`, { phone_number: testPhone }, { headers });
      toast.success("Test SMS sent successfully!");
      setTestDialogOpen(false);
      setTestPhone("");
      fetchData(); // Refresh logs
    } catch (error) {
      console.error("Error sending test SMS:", error);
      toast.error(error.response?.data?.detail || "Failed to send test SMS");
    } finally {
      setTestSending(false);
    }
  };

  const insertVariable = (variable) => {
    setFormData({
      ...formData,
      message_template: formData.message_template + variable,
    });
  };

  const previewMessage = () => {
    let preview = formData.message_template;
    preview = preview.replace("{customer_name}", "John Murphy");
    preview = preview.replace("{plan_name}", "Basic 30-Day");
    preview = preview.replace("{phone_number}", "+353851234567");
    preview = preview.replace("{plan_price}", "15.00");
    preview = preview.replace("{renewal_date}", "2026-02-10");
    return preview;
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="page-container" data-testid="sms-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="sms-settings-title">
              SMS Settings
            </h1>
            <p className="text-slate-400 mt-1">Configure renewal notification messages</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setTestDialogOpen(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="test-sms-btn"
            >
              <TestTube className="w-4 h-4" />
              Send Test SMS
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-settings-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Settings
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enable/Disable */}
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">SMS Notifications</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Send SMS to customers when their plan is renewed
                  </p>
                </div>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  className="data-[state=checked]:bg-[#00D26A]"
                  data-testid="sms-enabled-switch"
                />
              </div>
            </div>

            {/* Alpha Tag */}
            <div className="card p-6">
              <Label htmlFor="alpha_tag" className="text-white text-lg font-semibold">
                Sender ID (Alpha Tag)
              </Label>
              <p className="text-sm text-slate-400 mt-1 mb-3">
                The name that appears as the sender (3-11 characters)
              </p>
              <Input
                id="alpha_tag"
                value={formData.alpha_tag}
                onChange={(e) => setFormData({ ...formData, alpha_tag: e.target.value })}
                className="form-input max-w-xs"
                placeholder="BorrowAGeek"
                maxLength={11}
                data-testid="input-alpha-tag"
              />
            </div>

            {/* Message Template */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label htmlFor="message_template" className="text-white text-lg font-semibold">
                    Message Template
                  </Label>
                  <p className="text-sm text-slate-400 mt-1">
                    Use variables below to personalize the message
                  </p>
                </div>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-5 h-5 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 border-slate-700">
                    <p className="text-sm text-slate-200">
                      Click on a variable below to insert it into your message. 
                      Variables will be replaced with actual customer data when sending.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              
              {/* Variable Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                {AVAILABLE_VARIABLES.map((v) => (
                  <Tooltip key={v.name}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => insertVariable(v.name)}
                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded text-sm text-[#00D26A] font-mono transition-colors"
                        data-testid={`var-${v.name}`}
                      >
                        {v.name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 border-slate-700">
                      <p className="text-sm text-slate-200">{v.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
              
              <Textarea
                id="message_template"
                value={formData.message_template}
                onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                className="form-input min-h-[120px] font-mono text-sm"
                placeholder="Hi {customer_name}, your {plan_name} plan has been renewed..."
                data-testid="input-message-template"
              />
              
              <div className="mt-2 text-xs text-slate-500">
                {formData.message_template.length} characters
                {formData.message_template.length > 160 && (
                  <span className="text-yellow-500 ml-2">
                    (Message will be split into multiple SMS)
                  </span>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="card p-6 border-[#0066FF]/30 bg-[#0066FF]/5">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#0066FF]" />
                Message Preview
              </h3>
              <div className="bg-slate-950 rounded-lg p-4 border border-slate-800">
                <div className="text-xs text-slate-500 mb-2">From: {formData.alpha_tag || "BorrowAGeek"}</div>
                <p className="text-slate-200 text-sm whitespace-pre-wrap">{previewMessage()}</p>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                * Preview uses sample data. Actual messages will contain real customer information.
              </p>
            </div>
          </div>

          {/* SMS Logs */}
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#00D26A]" />
                  Recent SMS Logs
                </h3>
              </div>
              <div className="card-content p-0 max-h-[600px] overflow-y-auto">
                {logs.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No SMS sent yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {logs.map((log) => (
                      <div key={log.id} className="p-4 hover:bg-slate-800/30">
                        <div className="flex items-start justify-between mb-1">
                          <span className="font-medium text-white text-sm">{log.customer_name}</span>
                          {log.status === "sent" ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mb-1">{log.phone_number}</div>
                        <div className="text-xs text-slate-400 line-clamp-2">{log.message}</div>
                        {log.error && (
                          <div className="text-xs text-red-400 mt-1">{log.error}</div>
                        )}
                        <div className="text-xs text-slate-600 mt-2">
                          {format(new Date(log.created_at), "dd MMM HH:mm")}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Test SMS Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="test-sms-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <TestTube className="w-5 h-5 text-[#0066FF]" />
                Send Test SMS
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="test_phone" className="text-slate-300">Phone Number</Label>
                <Input
                  id="test_phone"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="form-input mt-1"
                  placeholder="+353851234567"
                  data-testid="input-test-phone"
                />
                <p className="text-xs text-slate-500 mt-1">Include country code (e.g., +353 for Ireland)</p>
              </div>
              <div className="bg-slate-800/50 rounded p-3 text-sm text-slate-400">
                A test message will be sent using your current Alpha Tag setting: <span className="text-white font-medium">{formData.alpha_tag || "BorrowAGeek"}</span>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setTestDialogOpen(false)}
                className="btn-ghost"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendTest}
                disabled={testSending}
                className="btn-primary"
                data-testid="send-test-btn"
              >
                {testSending ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
